import { CheckCircle2, Clock, Mail, RotateCcw } from "lucide-react";
import { NoiumWordmark } from "./shared";
import { getProposalTexts, type ProposalLanguage } from "./i18n";

export type LandingStateKind = "won" | "lost" | "expired";

type LandingStateProps = {
  kind: LandingStateKind;
  language: ProposalLanguage;
  clientCompany?: string | null;
};

// Full-screen "closed" landing pages shown in place of the live quote once an
// opportunity is won/lost or the quote has expired. The customer keeps the same
// /p/:slug link, so instead of a stale calculator we render a branded message:
//   • won     — celebratory, no CTA (we'll reach out)
//   • lost    — apologetic, mailto CTA to reopen the case
//   • expired — apologetic, mailto CTA to reopen the case
export function ProposalLandingState({ kind, language, clientCompany }: LandingStateProps) {
  const tx = getProposalTexts(language);
  const t = tx.landingStates;
  const copy = t[kind];
  const email = t.contactEmail;
  const isWon = kind === "won";

  const Icon = isWon ? CheckCircle2 : kind === "expired" ? Clock : Mail;

  const subject = encodeURIComponent(
    clientCompany
      ? language === "lt"
        ? `Pasiūlymo atnaujinimas — ${clientCompany}`
        : `Reopen proposal — ${clientCompany}`
      : language === "lt"
        ? "Pasiūlymo atnaujinimas"
        : "Reopen proposal"
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-brand-ink text-white">
      {/* Ambient brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, rgba(30,88,255,0.35) 0%, rgba(30,88,255,0) 70%), radial-gradient(50% 40% at 85% 30%, rgba(0,252,203,0.22) 0%, rgba(0,252,203,0) 70%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: "var(--brand-gradient)" }}
      />

      <div className="relative mx-auto flex min-h-screen max-w-3xl flex-col px-6 py-10 sm:px-10 sm:py-14">
        <NoiumWordmark dark />

        <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
          {/* Icon badge */}
          <div className="relative mb-9">
            <div
              aria-hidden
              className="absolute inset-0 -z-10 rounded-3xl blur-2xl"
              style={{
                background: isWon
                  ? "var(--brand-gradient)"
                  : "rgba(255,255,255,0.06)",
                opacity: isWon ? 0.5 : 1,
              }}
            />
            <div
              className="flex h-20 w-20 items-center justify-center rounded-3xl border"
              style={{
                borderColor: isWon
                  ? "rgba(0,252,203,0.4)"
                  : "rgba(255,255,255,0.12)",
                background: isWon
                  ? "linear-gradient(135deg, rgba(30,88,255,0.25), rgba(0,252,203,0.18))"
                  : "rgba(255,255,255,0.04)",
              }}
            >
              <Icon
                className="h-9 w-9"
                strokeWidth={1.75}
                style={{ color: isWon ? "#00fccb" : "#a1a1aa" }}
              />
            </div>
          </div>

          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
            {copy.eyebrow}
          </div>

          <h1
            className={`mt-4 max-w-2xl font-display text-3xl font-extrabold leading-[1.08] tracking-display sm:text-5xl ${
              isWon ? "brand-gradient-text" : "text-white"
            }`}
          >
            {copy.title}
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg">
            {copy.body}
          </p>

          {!isWon && "cta" in copy && (
            <a
              href={`mailto:${email}?subject=${subject}`}
              className="group mt-10 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold text-brand-ink transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--brand-gradient)" }}
            >
              <RotateCcw className="h-4 w-4" strokeWidth={2.25} />
              {copy.cta}
            </a>
          )}

          {!isWon && (
            <a
              href={`mailto:${email}?subject=${subject}`}
              className="mt-5 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white"
            >
              <Mail className="h-3.5 w-3.5" />
              {email}
            </a>
          )}
        </div>

        <div className="mt-auto border-t border-white/10 pt-6 text-center font-mono text-[10.5px] uppercase tracking-[0.16em] text-zinc-500">
          Noium · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
